import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useApi from '../api';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormatter';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Input } from "@/components/ui/input";
import { TabsContent } from "@/components/ui/tabs";
import { LoadingState } from '@/components/ui/LoadingState';
import { Eye, Search } from 'lucide-react';
import OrganizationCombobox from './OrganizationCombobox';

interface Organization {
  id: string;
  name: string;
}

interface Conversation {
  id: string;
  created_at: string;
  last_activity: string;
  status: string;
  organization?: {
    id: string;
    name: string;
  };
  lead?: {
    id: string;
    email: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
  };
  agent_id?: string;
}

const ConversationsTab: React.FC = () => {
  const { get } = useApi();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const selectedOrg = searchParams.get('org') || 'all';
  const setSelectedOrg = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next && next !== 'all') {
      params.set('org', next);
    } else {
      params.delete('org');
    }
    setSearchParams(params, { replace: false });
  };
  const [conversationIdQuery, setConversationIdQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalConversations, setTotalConversations] = useState(0);
  const perPage = 20;

  const handleFindConversationById = () => {
    const id = conversationIdQuery.trim();
    if (!id) {
      toast.error('Please enter a conversation ID');
      return;
    }
    navigate(`/admin/conversations/${id}`);
  };

  const fetchOrganizations = async () => {
    try {
      const response = await get('/admin/organizations/');
      setOrganizations(response.data || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    }
  };

  const fetchConversations = async (pageNum: number) => {
    try {
      setLoading(true);
      let url = `/sessions/?page=${pageNum}&per_page=${perPage}&is_admin=true`;
      if (selectedOrg !== 'all') {
        url += `&organization_id=${selectedOrg}`;
      }
      const response = await get(url);
      setConversations(response.data.items || []);
      setTotalPages(response.data.pages || 1);
      setTotalConversations(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    fetchConversations(page);
  }, [page, selectedOrg]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    } else {
      fetchConversations(1);
    }
  }, [selectedOrg]);

  const columns: ColumnDef<Conversation>[] = [
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => formatDate(row.original.created_at),
    },
    {
      accessorKey: 'organization',
      header: 'Organization',
      cell: ({ row }) => row.original.organization?.name || 'N/A',
    },
    {
      accessorKey: 'lead',
      header: 'Lead Email',
      cell: ({ row }) => row.original.lead?.email || 'Anonymous',
    },
    {
      accessorKey: 'agent_id',
      header: 'Agent',
      cell: ({ row }) => {
        const agentId = row.original.agent_id;
        return agentId ? agentId.substring(0, 8) + '...' : 'N/A';
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant={status === 'active' ? 'default' : 'secondary'}>
            {status}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/conversations/${row.original.id}`)}
        >
          <Eye className="h-4 w-4 mr-1" />
          View
        </Button>
      ),
    },
  ];

  const table = useReactTable({
    data: conversations,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading && conversations.length === 0) {
    return (
      <TabsContent value="conversations" className="space-y-4">
        <LoadingState label="Loading conversations..." className="min-h-80" />
      </TabsContent>
    );
  }

  return (
    <TabsContent value="conversations" className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <h2 className="text-xl font-semibold">
          All Conversations ({totalConversations})
        </h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
          <div className="flex items-center gap-2">
            <Input
              value={conversationIdQuery}
              onChange={(e) => setConversationIdQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFindConversationById();
                if (e.key === 'Escape') setConversationIdQuery('');
              }}
              placeholder="Find by conversation ID…"
              className="w-full sm:w-[300px]"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleFindConversationById}
              disabled={!conversationIdQuery.trim()}
              title="Open conversation by ID"
            >
              <Search className="h-4 w-4 mr-1" />
              Find
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Filter by Organization:</span>
            <OrganizationCombobox
              organizations={organizations}
              value={selectedOrg}
              onChange={setSelectedOrg}
            />
          </div>
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No conversations found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {page} of {totalPages}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
          >
            Next
          </Button>
        </div>
      </div>
    </TabsContent>
  );
};

export default ConversationsTab;

